import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import { Typography, colors, Tab, Grid, Table, Loading } from '@leapeasy/ui-kit';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getRequestLandlordAction, getRequestVPAction } from 'store/actions/delegationActions';
import { readStoredUser } from 'utils/authSession';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { getDelegationRequestTabs, canAccessDelegationRequest } from './delegationRequestTabs';
import {
  getRequestLandlordColumns,
  getRequestVPColumns,
  formatLandlordAddress,
  formatLandlordCreatedDate,
} from './requestTableColumns';
import { RequestPropertyModal } from './RequestPropertyModal';
import {
  pageHeaderSx,
  pageTitleStyle,
  contentContainerSx,
  helperTextStyle,
  tableWrapperSx,
} from 'containers/User/InviteNewUser/InvitePage/InvitePage.styles';
import { getRoleLabel } from 'constants/userRoles';
import { GlobalUXState } from 'components/GlobalUXState';
import { useDemoMask } from 'hooks/useDemoMask';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { maskDemoField } from 'utils/demoMask';

export const DelegationRequestPage = () => {
  const { isDemo } = useDemoMask();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const storedUser = readStoredUser();
  const currentUserState = useSelector((state) => state.getIn(['auth', 'currentUser']));
  const currentUser = currentUserState ? currentUserState.toJS() : storedUser;
  const currentUserRole = currentUser?.role;

  const requestLandlord = useSelector((state) => state.getIn(['delegation', 'requestLandlord']));
  const requestVP = useSelector((state) => state.getIn(['delegation', 'requestVP']));
  const { hasSuccess: hasSuccessNotification } = useNotificationMessages();

  const tabs = useMemo(() => getDelegationRequestTabs(currentUserRole), [currentUserRole]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [sortOptions, setSortOptions] = useState({});
  const [pagination, setPagination] = useState({
    pageNumber: 0,
    rowsPerPage: 12,
    maxVisiblePageItems: 6,
    onUpdate: (value) => {
      const nextPage = value === -1 ? 0 : value;
      setPagination((prev) => ({ ...prev, pageNumber: nextPage }));
    },
    showPageNumberInput: 6,
  });
  const [requestTarget, setRequestTarget] = useState(null);
  const [requestModalTitle, setRequestModalTitle] = useState('');
  const [awaitingSend, setAwaitingSend] = useState(false);

  const requestLandlordJSON = requestLandlord ? requestLandlord.toJS() : null;
  const requestVPJSON = requestVP ? requestVP.toJS() : null;
  const landlordRows = requestLandlordJSON?.data || [];
  const vpRows = requestVPJSON?.data || [];

  const landlordByKey = useMemo(() => {
    const map = new Map();
    landlordRows.forEach((row) => map.set(row.sf_landlord_id, row));
    return map;
  }, [landlordRows]);

  const vpByKey = useMemo(() => {
    const map = new Map();
    vpRows.forEach((row) => map.set(String(row.id), row));
    return map;
  }, [vpRows]);

  const refreshLists = useCallback(() => {
    dispatch(getRequestLandlordAction());
    dispatch(getRequestVPAction());
  }, [dispatch]);

  useEffect(() => {
    if (!canAccessDelegationRequest(currentUserRole)) return;
    refreshLists();
  }, [currentUserRole, refreshLists]);

  useEffect(() => {
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [selectedTab]);

  useEffect(() => {
    if (!awaitingSend || !hasSuccessNotification) return;
    setAwaitingSend(false);
    setRequestTarget(null);
    refreshLists();
  }, [awaitingSend, hasSuccessNotification, refreshLists]);

  const openLandlordRequest = useCallback(
    (landlordId) => {
      const landlord = landlordByKey.get(landlordId);
      if (!landlord) return;
      setRequestModalTitle(maskDemoField(isDemo, 'landlord-name', landlord.name || 'Operator'));
      setRequestTarget(landlord);
    },
    [landlordByKey, isDemo],
  );

  const openVpRequest = useCallback(
    (delegationId) => {
      const delegation = vpByKey.get(String(delegationId));
      if (!delegation) return;
      const name = delegation.accepter?.username || delegation.accepter_email;
      setRequestModalTitle(
        maskDemoField(
          isDemo,
          delegation.accepter?.username ? 'username' : 'email',
          name || 'Full Portfolio user',
        ),
      );
      setRequestTarget(delegation);
    },
    [vpByKey, isDemo],
  );

  const landlordTableData = useMemo(
    () =>
      landlordRows.map((landlord) => [
        landlord.sf_landlord_id,
        maskDemoField(isDemo, 'landlord-name', landlord.name || '-'),
        maskDemoField(isDemo, 'phone', landlord.phone || '-'),
        maskDemoField(isDemo, 'primary-contact', landlord.primary_contact || '-'),
        maskDemoField(isDemo, 'address', formatLandlordAddress(landlord)),
        maskDemoField(isDemo, 'email', landlord.email_address || '-'),
        landlord.number_of_residential_buildings ?? '-',
        landlord.number_of_units ?? '-',
        formatLandlordCreatedDate(landlord),
        landlord.sf_landlord_id,
        landlord.sf_landlord_id,
      ]),
    [landlordRows, isDemo],
  );

  const vpTableData = useMemo(
    () =>
      vpRows.map((delegation) => {
        const created = delegation.createdAt
          ? new Date(delegation.createdAt).toISOString().slice(0, 10)
          : '-';
        return [
          String(delegation.id),
          maskDemoField(isDemo, 'username', delegation.accepter?.username || '-'),
          maskDemoField(isDemo, 'phone', delegation.accepter?.phone || '-'),
          delegation.accepter?.job_title || '-',
          maskDemoField(isDemo, 'email', delegation.accepter_email || '-'),
          Array.isArray(delegation.property) ? delegation.property.length : 0,
          getRoleLabel(delegation.accepter_role),
          created,
          String(delegation.id),
          String(delegation.id),
        ];
      }),
    [vpRows, isDemo],
  );

  const landlordColumns = useMemo(
    () =>
      getRequestLandlordColumns({
        onRequest: openLandlordRequest,
        onRowDetail: (id) => navigate(`/user/operator/${id}/detail/report`),
      }),
    [openLandlordRequest, navigate],
  );

  const vpColumns = useMemo(
    () =>
      getRequestVPColumns({
        onRequest: openVpRequest,
        onRowDetail: (id) => {
          const delegation = vpByKey.get(String(id));
          if (delegation?.id) {
            navigate(`/user/full-portfolio/${delegation.id}/detail`);
          }
        },
      }),
    [openVpRequest, navigate, vpByKey],
  );

  const activeTabKey = tabs[selectedTab]?.key;
  const showLandlords = activeTabKey === 'landlords';
  const showVp = activeTabKey === 'vp';

  const landlordTotalItems = landlordTableData.length;
  const vpTotalItems = vpTableData.length;
  const activeTotalItems = showLandlords ? landlordTotalItems : vpTotalItems;

  useClampListPagination({
    pagination,
    setPagination,
    totalItems: activeTotalItems,
    isLoading: false,
  });

  const paginatedLandlordTableData = useMemo(() => {
    const start = pagination.pageNumber * pagination.rowsPerPage;
    return landlordTableData.slice(start, start + pagination.rowsPerPage);
  }, [landlordTableData, pagination.pageNumber, pagination.rowsPerPage]);

  const paginatedVpTableData = useMemo(() => {
    const start = pagination.pageNumber * pagination.rowsPerPage;
    return vpTableData.slice(start, start + pagination.rowsPerPage);
  }, [vpTableData, pagination.pageNumber, pagination.rowsPerPage]);

  const tablePagination = useMemo(
    () => ({
      ...pagination,
      totalItems: activeTotalItems,
      totalPages: Math.ceil(activeTotalItems / pagination.rowsPerPage) || 0,
    }),
    [activeTotalItems, pagination],
  );

  const shouldShowLandlordLoading = showLandlords && !requestLandlordJSON;
  const shouldShowVpLoading = showVp && !requestVPJSON;

  if (!canAccessDelegationRequest(currentUserRole)) {
    return (
      <DashboardLayoutContainer>
        <Box sx={pageHeaderSx}>
          <Typography variant="h3" style={{ color: colors.purple[900], ...pageTitleStyle }}>
            Request A Change
          </Typography>
        </Box>
        <Box padding="24px" sx={contentContainerSx}>
          <Typography variant="body2" style={{ color: colors.neutral[700] }}>
            Your account does not have access to delegation requests.
          </Typography>
        </Box>
      </DashboardLayoutContainer>
    );
  }

  return (
    <DashboardLayoutContainer>
      <RequestPropertyModal
        open={Boolean(requestTarget)}
        title={requestModalTitle}
        requestTarget={requestTarget}
        onClose={() => {
          setRequestTarget(null);
          setAwaitingSend(false);
        }}
        onSent={() => setAwaitingSend(true)}
      />

      <Box sx={pageHeaderSx}>
        <Typography variant="h3" style={{ color: colors.purple[900], ...pageTitleStyle }}>
          Request A Change
        </Typography>
      </Box>

      <Box padding="24px 12px" borderRadius="12px" mt={1} sx={contentContainerSx}>
        <Typography
          variant="body2"
          style={{ ...helperTextStyle, marginBottom: 16, display: 'block' }}
        >
          Request additional property access from a higher-level user. Select Request on a row,
          choose properties, and submit.
        </Typography>

        <Grid>
          {tabs.map((tab, index) => (
            <Tab
              key={tab.key}
              title={tab.title}
              isSelected={selectedTab === index}
              onClick={() => setSelectedTab(index)}
            />
          ))}
        </Grid>

        <Box marginTop="20px" sx={tableWrapperSx}>
          {showLandlords && (
            <>
              {shouldShowLandlordLoading ? (
                <Loading size="large" label="Loading operators..." />
              ) : (
                <Table
                  showEmptyState
                  emptyContent={
                    <GlobalUXState
                      variant="noData"
                      compact
                      description="No operators are available to request from."
                    />
                  }
                  data={paginatedLandlordTableData}
                  columns={landlordColumns}
                  sortOptions={sortOptions}
                  onChangeSort={setSortOptions}
                  pagination={tablePagination}
                  onChangeRowsPerPage={(value) =>
                    setPagination((prev) => ({
                      ...prev,
                      rowsPerPage: value,
                      pageNumber: 0,
                    }))
                  }
                />
              )}
            </>
          )}

          {showVp && (
            <>
              {shouldShowVpLoading ? (
                <Loading size="large" label="Loading full portfolio users..." />
              ) : (
                <Table
                  showEmptyState
                  emptyContent={
                    <GlobalUXState
                      variant="noData"
                      compact
                      description="No full portfolio users are available to request from."
                    />
                  }
                  data={paginatedVpTableData}
                  columns={vpColumns}
                  sortOptions={sortOptions}
                  onChangeSort={setSortOptions}
                  pagination={tablePagination}
                  onChangeRowsPerPage={(value) =>
                    setPagination((prev) => ({
                      ...prev,
                      rowsPerPage: value,
                      pageNumber: 0,
                    }))
                  }
                />
              )}
            </>
          )}
        </Box>
      </Box>
    </DashboardLayoutContainer>
  );
};
